/**
 * Format Discover Uni / HESA signals into a compact array of pill objects.
 *
 * Signals are only surfaced when their sample size clears a confidence
 * threshold (30) so we don't show noisy 5-student outcomes. Each returned
 * object is `{ key, label, value, title }` — `title` is a hover tooltip
 * that explains the sample size, keeping the row itself dense.
 *
 * Returns [] when no signal is trustworthy — the strip should render
 * nothing in that case.
 */
const MIN_SAMPLE = 30;
// LEO3 (3-year Longitudinal Educational Outcomes) is a smaller natural
// cohort than the 15-month Graduate Outcomes survey, so we relax the
// gate for the progression tooltip specifically.
const MIN_LEO3_SAMPLE = 20;

const nssAverage = (nss) => {
  if (!nss) return null;
  const keys = ["teaching", "opportunities", "feedback", "support", "organisation", "resources", "voice"];
  const nums = keys.map((k) => nss[k]).filter((n) => typeof n === "number");
  if (nums.length === 0) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
};

const shortGbp = (n) => {
  if (typeof n !== "number") return null;
  if (n >= 1000) return `£${Math.round(n / 1000)}k`;
  return `£${n}`;
};

const shortUsd = (n) => {
  if (typeof n !== "number") return null;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
};

const shortAud = (n) => {
  if (typeof n !== "number") return null;
  if (n >= 1000) return `A$${Math.round(n / 1000)}k`;
  return `A$${n}`;
};

export function formatHesaSignals(stats) {
  if (!stats || typeof stats !== "object") return [];
  const out = [];

  const salary = stats.salary_median_gbp;
  const salarySamp = stats.salary_sample_size;
  if (typeof salary === "number" && typeof salarySamp === "number" && salarySamp >= MIN_SAMPLE) {
    // If the course also carries a LEO3 (3-year) median with a trustworthy
    // sample size and the trajectory is positive, attach a progression
    // payload so the strip renders a Radix tooltip (same pattern used on
    // editorial ResultCards).
    const leo3 = stats.leo3_median_gbp;
    const leo3Samp = stats.leo3_sample_size;
    let progression = null;
    if (
      typeof leo3 === "number" &&
      typeof leo3Samp === "number" &&
      leo3Samp >= MIN_LEO3_SAMPLE &&
      leo3 > salary
    ) {
      progression = {
        startLabel: shortGbp(salary),
        endLabel: shortGbp(leo3),
        growthPct: Math.round(((leo3 - salary) / salary) * 100),
      };
    }
    out.push({
      key: "salary",
      label: `${shortGbp(salary)} median`,
      value: salary,
      progression,
      title: `Graduate Outcomes median salary 15 months after finishing. Sample size ${salarySamp}.`,
    });
  }

  const cont = stats.continuation_rate;
  const contSamp = stats.continuation_sample_size;
  if (typeof cont === "number" && typeof contSamp === "number" && contSamp >= MIN_SAMPLE) {
    out.push({
      key: "continuation",
      label: `${Math.round(cont * 100)}% continue`,
      value: cont,
      title: `Share of first-year students continuing into second year. Sample size ${contSamp}.`,
    });
  }

  const nss = stats.nss || {};
  const nssSamp = nss.sample_size;
  const nssAvg = nssAverage(nss);
  if (typeof nssAvg === "number" && typeof nssSamp === "number" && nssSamp >= MIN_SAMPLE) {
    out.push({
      key: "nss",
      label: `NSS ${nssAvg}`,
      value: nssAvg,
      title: `National Student Survey — average of 7 teaching-quality items (higher is better). Sample size ${nssSamp}.`,
    });
  }

  return out;
}


/**
 * Editorial "outcomes strip" for Advanced Search ResultCards.
 *
 * Editorial degrees carry a first-party `outcomes` object populated by our
 * editors from official sources (Graduate Outcomes, Prospects, LinkedIn
 * grad tracking). We surface up to 3 pills — median salary, employment
 * rate, highly-skilled %. Each pill is gated on `cohort_size ≥ 20` so we
 * don't show noise from tiny cohorts.
 *
 * `formatCurrency` should be the caller's currency formatter (from
 * useCurrency) so the salary respects the user's chosen currency toggle.
 */
const MIN_EDITORIAL_COHORT = 20;

export function formatEditorialOutcomes(outcomes, formatCurrency) {
  if (!outcomes || typeof outcomes !== "object") return [];
  const out = [];
  const cohort = outcomes.cohort_size;
  const canShow = typeof cohort === "number" && cohort >= MIN_EDITORIAL_COHORT;

  const salary = outcomes.median_starting_salary_usd;
  if (typeof salary === "number" && canShow) {
    const label = typeof formatCurrency === "function"
      ? `${formatCurrency(salary)} starting`
      : `$${Math.round(salary / 1000)}k starting`;
    // If the degree also carries a 3-year salary snapshot, attach it as a
    // progression payload so the strip can render a rich Radix tooltip
    // ("$52k → $73k @ 3y  · +40%").
    const y3 = outcomes.salary_after_3y_usd;
    let progression = null;
    if (typeof y3 === "number" && y3 > salary) {
      const growthPct = Math.round(((y3 - salary) / salary) * 100);
      const y3Label = typeof formatCurrency === "function"
        ? formatCurrency(y3)
        : `$${Math.round(y3 / 1000)}k`;
      progression = {
        startLabel: typeof formatCurrency === "function" ? formatCurrency(salary) : `$${Math.round(salary / 1000)}k`,
        endLabel: y3Label,
        growthPct,
      };
    }
    out.push({
      key: "starting_salary",
      label,
      value: salary,
      progression,
      title: `Median starting salary reported by graduates. Cohort size ${cohort}${outcomes.response_rate ? ` · response rate ${Math.round(outcomes.response_rate * 100)}%` : ""}.`,
    });
  }

  const emp = outcomes.employment_rate;
  if (typeof emp === "number" && canShow) {
    out.push({
      key: "employment",
      label: `${Math.round(emp * 100)}% in work or study`,
      value: emp,
      title: `Share of graduates in work OR further study 15 months out (HESA "activity" combined). Cohort size ${cohort}.`,
    });
  }

  const hs = outcomes.highly_skilled_employment_pct;
  if (typeof hs === "number" && canShow) {
    out.push({
      key: "highly_skilled",
      label: `${Math.round(hs * 100)}% highly-skilled`,
      value: hs,
      title: `Share of grads in graduate-level ("highly-skilled") roles per SOC classification. Cohort size ${cohort}.`,
    });
  }

  return out;
}


// ============================================================================
// Multi-source directory formatters (US Scorecard, AU QILT, FR Parcoursup)
// ============================================================================

const MIN_SCORECARD_SAMPLE = 30;
const MIN_QILT_SAMPLE = 30;

/**
 * US — College Scorecard field-of-study earnings and debt.
 * Y3 salary is linearly interpolated between Y1 and Y5 anchors at
 * ingestion time, so the progression tooltip lights up even though
 * Scorecard doesn't publish a discrete Y3 salary.
 */
export function formatScorecardSignals(stats) {
  if (!stats || typeof stats !== "object") return [];
  const out = [];
  const earn = stats.earn_mdn_hi_1yr_usd;
  const earnSamp = stats.earn_sample_size;
  const canShow = typeof earnSamp === "number" && earnSamp >= MIN_SCORECARD_SAMPLE;

  if (typeof earn === "number" && canShow) {
    const y3 = stats.earn_y3_interpolated_usd;
    const y5 = stats.earn_mdn_hi_5yr_usd;
    let progression = null;
    // Prefer Y5 → 5y label; fall back to interpolated Y3.
    if (typeof y5 === "number" && y5 > earn) {
      progression = {
        startLabel: shortUsd(earn),
        endLabel: shortUsd(y5),
        growthPct: Math.round(((y5 - earn) / earn) * 100),
        yearLabel: "@ 5y",
      };
    } else if (typeof y3 === "number" && y3 > earn) {
      progression = {
        startLabel: shortUsd(earn),
        endLabel: shortUsd(y3),
        growthPct: Math.round(((y3 - earn) / earn) * 100),
        yearLabel: "@ 3y (est.)",
      };
    }
    out.push({
      key: "scorecard_salary",
      label: `${shortUsd(earn)} y1 median`,
      value: earn,
      progression,
      title: `Median earnings 1 year after graduation, per U.S. Dept of Education College Scorecard. Sample size ${earnSamp}.`,
    });
  }

  const debt = stats.debt_median_usd ?? stats.debt_mean_usd;
  if (typeof debt === "number" && canShow) {
    out.push({
      key: "scorecard_debt",
      label: `${shortUsd(debt)} debt`,
      value: debt,
      title: `Median federal loan debt at graduation. From College Scorecard.`,
    });
  }
  return out;
}


/**
 * AU — QILT / GOS graduate outcomes signals.
 */
export function formatQiltSignals(stats) {
  if (!stats || typeof stats !== "object") return [];
  const out = [];
  const samp = stats.sample_size;
  const canShow = typeof samp === "number" && samp >= MIN_QILT_SAMPLE;
  if (!canShow) return out;

  const sal = stats.ft_salary_median_aud;
  if (typeof sal === "number") {
    out.push({
      key: "qilt_salary",
      label: `${shortAud(sal)} median`,
      value: sal,
      title: `Median full-time salary from QILT Graduate Outcomes Survey. Sample size ${samp}.`,
    });
  }

  const emp = stats.ft_employment_pct;
  if (typeof emp === "number") {
    out.push({
      key: "qilt_employment",
      label: `${Math.round(emp)}% ft-employed`,
      value: emp,
      title: `Share of graduates in full-time employment 4 months post-graduation. Sample size ${samp}.`,
    });
  }

  const sat = stats.overall_satisfaction_pct;
  if (typeof sat === "number") {
    out.push({
      key: "qilt_satisfaction",
      label: `${Math.round(sat)}% satisfied`,
      value: sat,
      title: `Overall satisfaction from QILT Student Experience Survey. Sample size ${samp}.`,
    });
  }
  return out;
}


/**
 * FR — Parcoursup competitive / composition signals.
 * No salary data — Parcoursup publishes admissions statistics only.
 */
export function formatParcoursupSignals(stats) {
  if (!stats || typeof stats !== "object") return [];
  const out = [];
  const sel = stats.selectivity_ratio;
  const cap = stats.capacity;

  if (typeof sel === "number" && typeof cap === "number" && cap >= 20) {
    const admitPct = Math.round(sel * 100);
    out.push({
      key: "parcoursup_selectivity",
      label: `${admitPct}% admit rate`,
      value: sel,
      title: `Ratio of admitted to offered capacity in the 2025 Parcoursup session. Capacity ${cap}.`,
    });
  }

  const bg = stats.pct_bac_general;
  if (typeof bg === "number") {
    out.push({
      key: "parcoursup_bac_g",
      label: `${Math.round(bg)}% bac général`,
      value: bg,
      title: `Share of admitted students holding a Bac Général (académique/prestige track).`,
    });
  }
  return out;
}


/**
 * Auto-router: given a directory row (whatever its country), returns the
 * appropriate signals array. Falls back to [] when nothing matches.
 */
export function formatDirectoryRowSignals(row) {
  if (!row || typeof row !== "object") return [];
  if (row.hesa_stats) return formatHesaSignals(row.hesa_stats);
  if (row.scorecard_stats) return formatScorecardSignals(row.scorecard_stats);
  if (row.qilt_stats) return formatQiltSignals(row.qilt_stats);
  if (row.parcoursup_stats) return formatParcoursupSignals(row.parcoursup_stats);
  return [];
}
